import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Trash2, CheckCircle, KeyRound } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../api';
import Avatar from '../components/Avatar';
import SocialIcon from '../components/SocialIcon';
import ActiveSessions from '../components/ActiveSessions';

const MIN_PASSWORD_LENGTH = 8;

const SecurityPage = () => {
    const { user, setUser } = useOutletContext();

    const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '' });
    const [twoFaInfo, setTwoFaInfo] = useState({ secret: null, otpauthUrl: null });
    const [twoFaCode, setTwoFaCode] = useState('');
    const [socialConnections, setSocialConnections] = useState([]);
    const [socialProviders, setSocialProviders] = useState([]);
    const [alert, setAlert] = useState(null); // { type: 'success' | 'error', message: '' }


    useEffect(() => {
        const fetchSocialConnections = () => {
            api.get('/api/users/me/social-connections')
                .then(res => setSocialConnections(res.data))
                .catch(() => setSocialConnections([]));
        };
        const fetchSocialProviders = () => {
            api.get('/api/auth/social-providers')
                .then(res => setSocialProviders(res.data))
                .catch(() => setSocialProviders([]));
        };
        fetchSocialConnections();
        fetchSocialProviders();
    }, []);

    // Привязанное показываем списком, остальное - кнопками. Провайдер, у которого уже
    // есть связь, во второй раз в кнопках не нужен.
    const availableProviders = socialProviders.filter(
        provider => !socialConnections.some(
            conn => (conn.provider || '').toLowerCase() === provider.id.toLowerCase()));

    const providerName = (id) => {
        const known = socialProviders.find(p => p.id.toLowerCase() === (id || '').toLowerCase());
        if (known) return known.name;
        // Провайдера могли убрать из настроек уже после того, как связь появилась.
        return id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Соцсеть';
    };

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000);
    };


    // У аккаунта, созданного через соцсеть, пароля нет: спрашивать «текущий» не о чем,
    // и форма превращается в установку пароля.
    const hasPassword = user?.hasPassword !== false;

    const handlePasswordChange = () => {
        if (hasPassword && !passwordForm.oldPassword) {
            showAlert('error', 'Введите текущий пароль');
            return;
        }
        if (passwordForm.newPassword.length < MIN_PASSWORD_LENGTH) {
            showAlert('error', 'Новый пароль должен быть не короче ' + MIN_PASSWORD_LENGTH + ' символов');
            return;
        }
        api.post('/api/users/change-password', passwordForm)
            .then(() => {
                setPasswordForm({ oldPassword: '', newPassword: '' });
                setUser(prev => ({ ...prev, hasPassword: true }));
                showAlert('success', hasPassword ? 'Пароль успешно изменен' : 'Пароль установлен');
            })
            .catch(err => showAlert('error', err.response?.data || 'Не удалось сменить пароль'));
    };

    const handleTwoFaSetup = () => {
        api.post('/api/users/me/2fa/setup')
            .then(res => {
                setTwoFaInfo(res.data);
                setTwoFaCode('');
            })
            .catch(() => showAlert('error', 'Не удалось инициализировать 2FA'));
    };

    const handleTwoFaEnable = () => {
        api.post('/api/users/me/2fa/enable', { code: twoFaCode })
            .then(() => {
                setUser(prev => ({ ...prev, twoFaEnabled: true }));
                setTwoFaInfo({ secret: null, otpauthUrl: null });
                setTwoFaCode('');
                showAlert('success', 'Двухфакторная аутентификация успешно включена');
            })
            .catch(() => showAlert('error', 'Неверный 2FA код'));
    };

    const handleTwoFaDisable = () => {
        if (!window.confirm('Вы уверены, что хотите отключить двухфакторную аутентификацию? Ваша учетная запись станет менее защищенной.')) return;
        
        api.post('/api/users/me/2fa/disable')
            .then(() => {
                setUser(prev => ({ ...prev, twoFaEnabled: false }));
                setTwoFaInfo({ secret: null, otpauthUrl: null });
                setTwoFaCode('');
                showAlert('success', 'Двухфакторная аутентификация отключена');
            })
            .catch(() => showAlert('error', 'Не удалось отключить 2FA'));
    };

    const handleUnlinkSocial = (id) => {
        if (!window.confirm('Вы уверены, что хотите отвязать этот аккаунт?')) return;
        
        api.delete(`/api/users/me/social-connections/${id}`)
            .then(() => {
                setSocialConnections(prev => prev.filter(c => c.id !== id));
                showAlert('success', 'Социальный аккаунт успешно отвязан');
            })
            .catch(() => showAlert('error', 'Не удалось отвязать аккаунт'));
    };


    if (!user) return null;

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">Безопасность и вход</h1>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">
                    {hasPassword ? 'Смена пароля' : 'Пароль'}
                </h3>

                {!hasPassword && (
                    <div className="p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 bg-blue-50 text-blue-900 border border-blue-200">
                        <KeyRound size={20} />
                        <span>
                            У аккаунта нет пароля - вы входите через соцсеть. Задайте пароль, чтобы
                            входить ещё и по адресу почты.
                        </span>
                    </div>
                )}

                {hasPassword && (
                    <div className="mb-4 flex flex-col gap-1.5 text-left">
                        <label className="text-sm font-medium text-gray-700">Текущий пароль</label>
                        <input
                            className="px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10"
                            type="password"
                            value={passwordForm.oldPassword}
                            onChange={e => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                        />
                    </div>
                )}
                <div className="mb-4 flex flex-col gap-1.5 text-left">
                    <label className="text-sm font-medium text-gray-700">
                        {hasPassword ? 'Новый пароль' : 'Пароль'}
                    </label>
                    <input
                        className="px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    />
                    <span className="text-[13px] text-gray-500">Не короче {MIN_PASSWORD_LENGTH} символов.</span>
                </div>
                <div className="flex gap-3 mt-4">
                    <button className="bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400" onClick={handlePasswordChange}>
                        {hasPassword ? 'Изменить пароль' : 'Установить пароль'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Двухфакторная аутентификация (2FA)</h3>
                
                {user.twoFaEnabled ? (
                    <div className="p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <ShieldCheck size={20} />
                        <span>Двухфакторная аутентификация включена. Ваш аккаунт надежно защищен.</span>
                    </div>
                ) : (
                    <div className="p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 bg-amber-50 text-amber-800 border border-amber-200">
                        <AlertTriangle size={20} />
                        <span>Двухфакторная аутентификация выключена. Рекомендуем включить для дополнительной защиты.</span>
                    </div>
                )}

                {!user.twoFaEnabled && !twoFaInfo.secret && (
                    <button className="bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700" onClick={handleTwoFaSetup}>
                        Настроить 2FA
                    </button>
                )}

                {!user.twoFaEnabled && twoFaInfo.secret && (
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="m-0 font-medium text-gray-700">
                                1. Отсканируйте QR-код в приложении-аутентификаторе (Google Authenticator, Authy и т.д.)
                            </p>
                            <QRCodeCanvas value={twoFaInfo.otpauthUrl} size={180} level={"M"} includeMargin={true} />
                            
                            <p className="m-0 mt-2 font-medium text-gray-700">
                                Или введите секретный ключ вручную:
                            </p>
                            <div className="font-mono bg-gray-200 px-3 py-1.5 rounded-md text-base tracking-wide text-gray-900">{twoFaInfo.secret}</div>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">2. Введите 6-значный код из приложения</label>
                            <input
                                className="px-3.5 py-2.5 border border-gray-300 rounded-lg text-lg tracking-[4px] text-center w-[200px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10"
                                type="text"
                                placeholder="000000"
                                value={twoFaCode}
                                onChange={e => setTwoFaCode(e.target.value)}
                                maxLength={6}
                            />
                        </div>
                        <div className="flex gap-3 mt-4">
                            <button className="bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed" onClick={handleTwoFaEnable} disabled={twoFaCode.length < 6}>
                                Подтвердить и включить
                            </button>
                            <button className="bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400" onClick={() => setTwoFaInfo({ secret: null, otpauthUrl: null })}>
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                {user.twoFaEnabled && (
                    <button className="bg-transparent text-red-500 px-3.5 py-2 border border-red-300 rounded-md text-[13px] font-medium cursor-pointer transition-all hover:bg-red-50 hover:border-red-500" onClick={handleTwoFaDisable}>
                        Отключить 2FA
                    </button>
                )}
            </div>

            <div className="bg-white rounded-xl mb-6 shadow-sm border border-gray-200 overflow-hidden">
                <h3 className="m-0 px-6 py-4 text-[15px] font-semibold text-gray-900">Способы авторизации</h3>

                {socialConnections.map(conn => (
                    <div key={conn.id} className="flex items-center gap-3 px-6 py-3.5 border-t border-gray-200">
                        <SocialIcon provider={conn.provider} size={20} />
                        <Avatar
                            src={conn.avatarUrl}
                            name={conn.displayName}
                            email={conn.email}
                            size={32}
                        />
                        <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                            <span className="text-sm text-gray-900 truncate">
                                {conn.displayName || conn.email || providerName(conn.provider)}
                            </span>
                            {conn.displayName && conn.email && (
                                <span className="text-xs text-gray-500 truncate">{conn.email}</span>
                            )}
                        </div>
                        <button
                            className="bg-transparent text-gray-400 p-2 border border-transparent rounded-md cursor-pointer transition-all hover:text-red-600 hover:border-red-200 hover:bg-red-50 shrink-0"
                            title={`Отвязать ${providerName(conn.provider)}`}
                            onClick={() => handleUnlinkSocial(conn.id)}
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}

                {/*
                    Кнопки только для того, что ещё не привязано. Провайдеры приходят с
                    сервера: нарисованная здесь кнопка провайдера, которого нет в
                    настройках, вела бы на несуществующий адрес и отличалась бы от рабочей
                    только тем, что ломается после нажатия.
                */}
                {availableProviders.length > 0 && (
                    <div className="flex flex-wrap gap-3 px-6 py-4 border-t border-gray-200">
                        {availableProviders.map(provider => (
                            <a
                                key={provider.id}
                                href={provider.authorizationUrl + '?link=1'}
                                className="flex items-center gap-2 bg-white text-gray-700 px-3.5 py-2 border border-gray-300 rounded-lg text-sm font-medium no-underline transition-all hover:bg-gray-50 hover:border-gray-400"
                            >
                                <SocialIcon provider={provider.id} size={20} />
                                {provider.name}
                            </a>
                        ))}
                    </div>
                )}

                {socialConnections.length === 0 && availableProviders.length === 0 && (
                    <p className="px-6 py-4 border-t border-gray-200 text-sm text-gray-500 m-0">
                        Вход через социальные сети не настроен.
                    </p>
                )}
            </div>
            
            <ActiveSessions accountId={user.id} />
        </div>
    );
};

export default SecurityPage;
